<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import { coverageMayHaveChanged, installedAccounts, resetCoverage, uncoveredRepos, type CoverageSignal } from '../lib/api/install';
  import { installUrl } from '../lib/api/errors';
  import { failureOf } from '../lib/api/failure';
  import { failed, has, statusOf } from '../lib/runtime/remote';
  import LoadError from '../lib/components/LoadError.svelte';
  import { PERSONAL_POLICY } from '../lib/api/server';
  import { configureUrl, onboarding, policyHref, type AccountsView, type InstalledAccount } from '../lib/logic/onboarding';
  import { policySummary } from '../lib/logic/policy';
  import type { DashPr } from '../lib/api/queries';
  import { relTime } from '../lib/logic/time';
  import { branchQuietLine } from '../lib/logic/branches';
  import { yoursWithoutPull } from '../lib/state/branches.svelte';
  import { hasMember, reasonText, sameLogin } from '../lib/logic/attention';
  import { rowWait } from '../lib/logic/wait';
  import { involvement, involvementText, involvementTitle } from '../lib/logic/involvement';
  /** A row's wait, explained when it is a lower bound ("2d+"). */
  const waitTitle = (w: string | null) => (w?.endsWith('+') ? 'At least this long: the turn began before the part of the timeline Changeling read' : undefined);
  import { gapChip } from '../lib/logic/attentionGap';
  import { session } from '../lib/state/session.svelte';
  import { FilterSync, type SaveStatus } from '../lib/state/dashFilterSync';
  import { settings } from '../lib/state/settings.svelte';
  import { getPreferences, putPreference } from '../lib/api/server';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import { DashboardStore, readRows, searchSection, type DashRow } from '../lib/state/dashboard.svelte';
  import { dashboardStorage, sectionKey } from '../lib/state/dashCache';
  import { eng } from '../lib/diff/engine';
  import { LiveEvents } from '../lib/api/sse';
  import { prefs } from '../lib/state/prefs.svelte';
  import { isFolded, stackSection, toggleFold, type Folds, type MissingNode, type PrNode, type Stack } from '../lib/logic/stacks';
  import {
    dashStops,
    decodeFoldState,
    encodeFoldState,
    sectionIds,
    settle,
    type RailItem,
    type SectionLayout,
    type Stop,
  } from '../lib/logic/dashFocus';
  import { nav } from '../lib/nav.svelte';
  import { DASH_GUIDE } from '../lib/keys/guides';
  import { keyboard, useKeys, viewModel, type ViewModel } from '../lib/keys/keyboard.svelte';
  import { currentStop, updateDashboard, type DashEffect, type DashEnv, type DashMsg, type DashState } from '../lib/commands/dashboard';
  import type { Command } from '../lib/commands/command';
  import ContextMember from '../lib/components/ContextMember.svelte';
  import DashFilterBar from '../lib/components/DashFilterBar.svelte';
  import AuthorMark from '../lib/components/AuthorMark.svelte';
  import SingleLead from '../lib/components/SingleLead.svelte';
  import LabelList from '../lib/components/LabelList.svelte';
  import RepoMark from '../lib/components/RepoMark.svelte';
  import { prKind } from '../lib/logic/prKind';
  import { shapesOf } from '../lib/logic/dashShapes';
  import {
    countText,
    leftOut,
    facetOptions,
    filterRows,
    filterSearch,
    isActive,
    normalize,
    loadCached,
    FILTER_PREFERENCE,
    NO_FILTER,
    parseFilterQuery,
    rowSubject,
    sameFilter,
    saveCached,
    toggle as toggleValue,
    serverFilter,
    viewOf,
    waitUnknown,
    type DashFilter,
    type Filtered,
  } from '../lib/logic/dashFilter';
  import { checkState, mapCells, railLines, rowReview, stateText, type CheckState, type MapCell, type MemberState, type RowReview } from '../lib/logic/stackState';
  import { prSummaries } from '../lib/state/prSummaries.svelte';
  import StackMap from '../lib/components/StackMap.svelte';
  import DashMap from '../lib/components/DashMap.svelte';
  import { rowInvolve, rowTone, topInvolve, topTone } from '../lib/logic/dashMap';
  import ReviewDetail from '../lib/components/ReviewDetail.svelte';
  import { belowStart, buildsOnText, compactCount, rail, turn, type RailAside, type RailPr, type RailRow } from '../lib/logic/stackRail';
  import { prPath } from '../lib/router';
  import {
    DEFAULT_SECTIONS,
    formatSections,
    loadSections,
    parseSections,
    saveSections,
    partialWhat,
    checkingWhat,
    type Section,
  } from '../lib/logic/sections';

  let sections: Section[] = $state(loadSections());
  let editing = $state(false);
  let editText = $state('');
  let accounts = $state<AccountsView>({ status: 'loading' });
  const me = $derived(session.user?.login ?? '');
  const dash = new DashboardStore({
    me: () => me,
    search: searchSection,
    attention: eng.attention,
    live: (topics, handler) =>
      new LiveEvents(
        topics,
        (topic, notice) => {
          // The user's filter changed on another page of theirs (or in the extension): read it again.
          if (notice?.kind === 'preferences') {
            void filterSync.refresh();
            settings.refresh();
          }
          // Back on the page, or the stream came back: the App may have been installed meanwhile.
          if (notice === null && (topic === 'focus' || topic === 'reconnect' || topic === 'resync')) coverageSignal({ kind: topic });
          handler(topic, notice);
        },
        (_topic, reason) => coverageSignal({ kind: 'revoked', reason }),
      ),
    snapshot: dashboardStorage(location.origin),
    rows: readRows,
  });
  const stacked = $derived(prefs.value.dashboardView !== 'flat');
  const FOLDS_KEY = 'dashboard-folds';
  function savedFolds() {
    try {
      return decodeFoldState(sessionStorage.getItem(FOLDS_KEY));
    } catch {
      return decodeFoldState(null);
    }
  }
  const foldState = savedFolds();
  /** Stacks folded to their summary row, by section ID and membership; kept for the tab with `expanded`. */
  let folds: Folds = $state.raw(foldState.folds);
  /** Runs of members outside the section shown one by one, by stop (section ID, stack and starting position). */
  let expanded: ReadonlySet<string> = $state.raw(foldState.runs);
  $effect(() => {
    const text = encodeFoldState({ folds, runs: expanded });
    try {
      sessionStorage.setItem(FOLDS_KEY, text);
    } catch {
      /* storage unavailable: folds last as long as the page */
    }
  });
  /** Each section's ID (its definition's): stop keys and folds name sections by it, so editing the others moves neither. */
  const sids = $derived(sectionIds(dash.sections.map(sectionKey)));

  // The filter (logic/dashFilter.ts). The user's own is kept on the server and shared with their other browsers
  // and the extension (`FilterSync`): only the controls here change it. The URL shows the filter in view, so a
  // filtered dashboard can be bookmarked or sent to someone: a URL that names another filter than the user's
  // shows it for this view alone (`viewOf`), and stores nothing.
  let mine: DashFilter = $state.raw(NO_FILTER);
  let saveStatus: SaveStatus = $state('saved');
  const writeFilter = (f: DashFilter) => nav.go(nav.pathname + filterSearch(nav.search, f), { replace: true, keepScroll: true });
  const filterSync = new FilterSync({
    read: async () => {
      const p = await getPreferences();
      return serverFilter(p.preferences, p.revisions);
    },
    write: async (f, from) => {
      const p = await putPreference(FILTER_PREFERENCE, f, from);
      return p === 'conflict' ? p : { rev: serverFilter(p.preferences, p.revisions).rev };
    },
    cache: { load: loadCached, save: saveCached },
    onchange: (next) => {
      // The URL follows the user's filter while it is the one in view; a link's filter stays in view.
      const following = nav.route.name === 'dashboard' && !viewOf(parseFilterQuery(nav.query), mine).shared;
      mine = next.current;
      saveStatus = next.status;
      if (following && !sameFilter(parseFilterQuery(nav.query) ?? NO_FILTER, mine)) writeFilter(mine);
    },
    now: Date.now,
  });
  // Before the first render, so this browser's copy never flashes in.
  mine = filterSync.open();
  const urlFilter = $derived(parseFilterQuery(nav.query));
  const view = $derived(viewOf(urlFilter, mine));
  const filter: DashFilter = $derived(view.filter);
  const filtering = $derived(isActive(filter));
  /** A dashboard URL that names no filter is given the user's own, so the address always says what is in view. */
  function nameMine() {
    if (nav.route.name === 'dashboard' && parseFilterQuery(nav.query) === null && isActive(mine)) writeFilter(mine);
  }
  nameMine();
  $effect(nameMine);
  /** The user's choice, with the controls: their filter from now on, here and everywhere. */
  function setFilter(f: DashFilter) {
    filterSync.set(f);
    writeFilter(f);
  }
  /** Labels a row shows before "+N". */
  const ROW_LABELS = 3;
  /** A label's chip on a row: the label joins the filter in view (and stays when it is there already). */
  function addLabel(name: string) {
    if (!filter.label.includes(name.trim().toLowerCase())) setFilter(toggleValue(filter, 'label', name));
  }
  let filterBar: DashFilterBar | undefined = $state();
  /** What the filter shows, for the bar's live region: nothing to say without one. */
  const filterSummary = $derived.by(() => {
    if (!filtering) return '';
    const shown = filtered.reduce((n, f) => n + (f?.rows.length ?? 0), 0);
    const total = filtered.reduce((n, f) => n + (f?.total ?? 0), 0);
    return `Showing ${shown} of ${total} rows across the sections`;
  });
  /** Each section's rows under the filter; null while it has none. */
  const filtered: (Filtered<DashRow> | null)[] = $derived(dash.state.map((st) => (st.tag === 'Has' ? filterRows(st.value.rows, filter, rowSubject) : null)));
  /** The overview's filters (logic/dashMap.ts): a failing run's shows only the pull requests whose checks are failing. */
  const mapFilters = $derived({
    failing: { title: 'Show only pull requests whose checks are failing', apply: () => setFilter(normalize({ ...filter, ci: ['failing'] })) },
  });
  /** What the pickers offer: counted over every section's rows, each pull request once. */
  const facets = $derived(facetOptions(dash.state.flatMap((st) => (st.tag === 'Has' ? st.value.rows.map(rowSubject) : [])), filter, me));

  // Whose a row is: its repository owner's avatar, from the row, else from the App's installations.
  const orgAvatars = $derived.by(() => {
    const by = new Map<string, string>();
    if (accounts.status === 'known' && accounts.onboarding.state !== 'not_installed') for (const a of accounts.onboarding.accounts) if (a.avatarUrl) by.set(a.login.toLowerCase(), a.avatarUrl);
    for (const r of dash.pool) {
      const o = r.pr.repository.owner;
      if (o.avatarUrl) by.set(o.login.toLowerCase(), o.avatarUrl);
    }
    return by;
  });
  const ownerOf = (repo: string) => repo.split('/')[0];
  const avatarOf = (login: string) => orgAvatars.get(login.toLowerCase()) ?? null;

  /** Repositories (lowercased `owner/name`) the Changeling GitHub App is known not to cover (`uncoveredRepos`). */
  let uncovered: ReadonlySet<string> = $state.raw(new Set());
  /** Counts the times coverage was asked of GitHub afresh: what was worked out before is worked out again. */
  let coverageAsked = $state(0);
  let coverageAskedAt = Date.now();
  /** The installations may have changed under the page (`coverageMayHaveChanged`): ask again, quietly, and re-tag the rows. */
  function coverageSignal(signal: CoverageSignal) {
    if (!coverageMayHaveChanged(signal, coverageAskedAt, Date.now())) return;
    void loadAccounts(true, true);
  }
  const shownRepos = $derived([...new Map(dash.pool.map((r) => [r.pr.repository.nameWithOwner.toLowerCase(), r.pr.repository])).values()]);
  const reposKey = $derived(shownRepos.map((r) => `${r.nameWithOwner}|${r.isPrivate}|${r.viewerPermission}`).sort().join('\n'));
  $effect(() => {
    void reposKey;
    // Asked again once the installations are (after Check again, a Refresh, or a sign that they changed).
    void accounts.status;
    void coverageAsked;
    let current = true;
    void uncoveredRepos(untrack(() => shownRepos)).then((set) => {
      if (current) uncovered = set;
    });
    return () => {
      current = false;
    };
  });
  const isUncovered = (repo: string) => uncovered.has(repo.toLowerCase());
  const UNCOVERED_TITLE = (repo: string) =>
    `The Changeling GitHub App isn't installed on ${repo}. You can read this pull request here, but writes (comments, reviews, attention changes) and live updates won't work until it is installed there.`;

  /** Show the saved dashboard for these sections at once, checking GitHub when it is old (or `revalidate`). */
  function open(revalidate = false) {
    void dash.open(sections, { revalidate });
  }

  /** Refresh from GitHub: every section and the accounts, past every kept copy. */
  function refresh() {
    void dash.refresh();
    void loadAccounts(true);
  }

  /** The clock "updated … ago" is read against, ticking while the page shows. */
  let now = $state(Date.now());
  const fresh = $derived(dash.freshness);
  const ago = (at: number) => relTime(new Date(at).toISOString(), now);

  /** Ask GitHub for the App's installations; `again` drops the page's cached answer first; `quiet` keeps the list on screen meanwhile. */
  async function loadAccounts(again = false, quiet = false) {
    if (again) {
      resetCoverage();
      coverageAskedAt = Date.now();
      coverageAsked++;
    }
    if (!quiet) accounts = { status: 'loading' };
    try {
      const viewer = session.user;
      const list = viewer ? await installedAccounts({ id: viewer.id, login: viewer.login }) : null;
      accounts = list ? { status: 'known', onboarding: onboarding(list) } : { status: 'unavailable' };
    } catch (e) {
      accounts = { status: 'error', failure: failureOf(e) };
    }
  }

  /** The sections were asked for: until then there is nothing of them to show. */
  let asked = $state(false);
  onMount(() => {
    open();
    asked = true;
    void loadAccounts();
    dash.subscribe(session.user?.id ?? null);
    const tick = setInterval(() => (now = Date.now()), 15_000);
    return () => {
      clearInterval(tick);
      dash.dispose();
      filterSync.close();
    };
  });

  function startEdit() {
    editText = formatSections(sections);
    editing = true;
  }

  function saveEdit() {
    const parsed = parseSections(editText);
    if (!parsed.length) return;
    sections = parsed;
    saveSections(parsed);
    editing = false;
    open(true);
  }

  function reset() {
    sections = DEFAULT_SECTIONS;
    saveSections(null);
    editing = false;
    open(true);
  }

  const CHECK_TEXT: Record<CheckState, string> = { passing: 'checks passing', failing: 'checks failing', pending: 'checks pending', none: 'no checks' };
  const ciOf = (p: DashPr) => checkState(p.commits.nodes[0]?.commit.statusCheckRollup?.state);

  /** A row's review state (its node's fill): from its reviewers, as its avatars show them. */
  /** Each row's review state, worked out once per row (and as a context row or not): its node, line, map cell, pill and avatars' notes all read it. */
  let reviews = new WeakMap<DashRow, { own?: RowReview; context?: RowReview }>();
  let reviewsFor = '';
  function reviewOf(row: DashRow, context: boolean): RowReview {
    // The viewer's turn depends on who the viewer is.
    if (reviewsFor !== me) {
      reviews = new WeakMap();
      reviewsFor = me;
    }
    const kept = reviews.get(row) ?? {};
    const slot = context ? 'context' : 'own';
    let r = kept[slot];
    if (!r) {
      r = rowReview({
        state: row.pr.state,
        review: row.review,
        decision: row.pr.reviewDecision,
        turn: turn(row.attention, row.pr.author?.login ?? null, me).kind,
        context,
      });
      reviews.set(row, { ...kept, [slot]: r });
    }
    return r;
  }
  const rowMemberState = (row: DashRow, context: boolean): MemberState => reviewOf(row, context).member;
  /** A stack member outside the section: its pill, when it is loaded. */
  const memberPill = (m: PrNode<DashRow> | MissingNode<DashRow>) => (m.kind === 'pr' ? reviewOf(m.row, true).pill : null);
  const prState = (r: RailPr<DashRow>): MemberState => rowMemberState(r.node.row, r.node.context);
  /** A rail row's state: a gathered row's is its first member's. */
  const rowState = (r: RailRow<DashRow>): MemberState =>
    r.kind === 'pr' ? prState(r) : r.members[0].kind === 'missing' ? 'missing' : 'context';

  /** The stack map's cell in words: position, pull request and title when known, and state. */
  function cellLabel(st: Stack<DashRow>, rows: RailRow<DashRow>[], c: MapCell): string {
    const r = rows[c.row];
    const member = r.kind === 'pr' ? r.node : r.members.find((m) => m.index === c.index);
    const title = member?.kind === 'pr' ? member.row.pr.title : null;
    const start = r.kind === 'pr' && st.start === r.node ? ', start here' : '';
    return `${c.index} of ${st.count}: ${c.pr ? prLabel(st, c.pr) : 'unnumbered'}${title ? ` ${title}` : ''}, ${stateText(c.state)}${start}`;
  }

  /** Show stack `st`'s row `k` (unfolding it first) and focus its link. */
  async function goToRow(si: number, st: Stack<DashRow>, id: string, k: number) {
    if (isFolded(folds, sids[si], st)) folds = toggleFold(folds, sids[si], st);
    await tick();
    const li = document.getElementById(`${id}-row-${k}`);
    if (!li) return;
    li.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    (li.querySelector<HTMLElement>('[data-stop]') ?? li).focus({ preventScroll: true });
  }

  /** A pull request as a stack shows it: the repository only in a stack across several. */
  const prLabel = (st: Stack<DashRow>, x: { repo: string; number: number }) => `${st.repos.length > 1 ? x.repo : ''}#${x.number}`;

  // The keyboard cursor (dashFocus.ts): one stop per row, stack head and run
  // of members outside the section. Each stop's main control carries
  // `data-stop` and is the list's one tab stop while the cursor is on it
  // (a roving tab index); its row carries `data-stop-row`.

  /** A pull request's stop in section `sid`, the same in Stacked and Flat and wherever it sits in a stack. */
  const prStop = (sid: string, repo: string, number: number) => `${sid}/pr/${repo}#${number}`.toLowerCase();
  const stackStop = (sid: string, st: Stack<DashRow>) => `${sid}/stack/${st.key}`;
  /** A member outside the section: by number when it has one (it keeps its stop once read), else by stack position. */
  function memberStop(sid: string, st: Stack<DashRow>, m: PrNode<DashRow> | MissingNode<DashRow>): string {
    if (m.kind === 'pr') return prStop(sid, m.row.pr.repository.nameWithOwner, m.row.pr.number);
    if (m.first !== null && m.first === m.last && m.from === m.to) return prStop(sid, m.repo, m.first);
    return `${sid}/pos/${st.key}/${m.index}`;
  }
  /** A run by its stack and starting position, which reading its members leaves alone. */
  const runStop = (sid: string, st: Stack<DashRow>, r: RailAside<DashRow>) => `${sid}/run/${st.key}/${r.index}`;
  const rowStop = (sid: string, st: Stack<DashRow>, r: RailRow<DashRow>) =>
    r.kind === 'pr' ? prStop(sid, r.node.row.pr.repository.nameWithOwner, r.node.row.pr.number) : r.members.length > 1 ? runStop(sid, st, r) : memberStop(sid, st, r.members[0]);

  type StackView = { kind: 'stack'; stack: Stack<DashRow>; key: string; open: boolean; rows: RailRow<DashRow>[] };
  type EntryView = { kind: 'single'; row: DashRow; key: string } | StackView;
  /** Each section's entries as shown: Stacked or Flat, stacks with their rails. */
  const views: EntryView[][] = $derived(
    dash.sections.map((_, si) => {
      const st = dash.state[si];
      const sid = sids[si];
      if (st?.tag !== 'Has') return [];
      // Under the filter: the matching rows. A stack shows when one of them is in it, and its members the filter
      // left out keep their places as thin context rows.
      const rows = filtered[si]?.rows ?? st.value.rows;
      const single = (row: DashRow): EntryView => ({ kind: 'single', row, key: prStop(sid, row.pr.repository.nameWithOwner, row.pr.number) });
      if (!stacked) return rows.map(single);
      return stackSection(rows, dash.pool, me, dash.hidden, dash.sso, leftOut(filter, rowSubject)).map((e) =>
        e.kind === 'single' ? single(e.row) : { kind: 'stack', stack: e, key: stackStop(sid, e), open: !isFolded(folds, sid, e), rows: rail(e) },
      );
    }),
  );
  /** Each section's items as shapes: stacks as cards, runs of singles as one list (logic/dashShapes.ts). */
  const shapes = $derived(views.map((entries) => shapesOf(entries.map((e) => e.kind))));
  const layout: SectionLayout[] = $derived(
    views.map((entries, si) => ({
      items: entries.map((e) =>
        e.kind === 'single'
          ? { kind: 'row' as const, key: e.key }
          : {
              kind: 'stack' as const,
              key: e.key,
              open: e.open,
              rows: e.rows.map((r): RailItem => {
                const key = rowStop(sids[si], e.stack, r);
                if (r.kind === 'pr' || r.members.length === 1) return { kind: 'row', key };
                return { kind: 'run', key, open: expanded.has(key), members: r.members.map((m) => memberStop(sids[si], e.stack, m)) };
              }),
            },
      ),
    })),
  );
  const stops: Stop[] = $derived(dashStops(layout));

  const CURSOR_KEY = 'dashboard-cursor';
  function savedCursor(): string | null {
    try {
      return sessionStorage.getItem(CURSOR_KEY);
    } catch {
      return null;
    }
  }
  /** The stop the keys work from: kept through refreshes and, in this tab, reloads. */
  // The cursor is the dashboard's model, held by a Program: commands move it, and so do the focus going to a
  // row and its row going away (`rest`); nothing else writes it.
  const model: ViewModel<DashState, DashMsg> = viewModel({
    init: { cursor: savedCursor() } as DashState,
    env: (): DashEnv => ({ stops, focused: focusedRow()?.dataset.stopRow ?? (focusedRow() ? currentStop(model.state, stops) : null), refreshing: dash.refreshing }),
    update: updateDashboard,
    perform: (e: DashEffect) => perform(e),
  });
  const cursor = $derived(model.state.cursor);
  /** The stop that takes the Tab key: the cursor's, or the first while the cursor's is not shown. */
  const current = $derived(currentStop(model.state, stops));
  /** The top-level stop the cursor is in (its stack's head, for a row of a stack): that stack's other controls take Tab too. */
  const cursorTop = $derived.by(() => {
    const byKey = new Map(stops.map((x) => [x.key, x]));
    let at = current ? byKey.get(current) : undefined;
    while (at?.parent) at = byKey.get(at.parent);
    return at?.key ?? null;
  });
  /** Whether the keyboard focus is on a stop, to draw the cursor's ring. */
  let ring = $state(false);
  /** Whether the focus is in the list: only then does it follow the cursor when the cursor's row goes away. */
  let inList = false;
  let list: HTMLElement | undefined = $state();

  let before: Stop[] = [];
  $effect(() => {
    const now = stops;
    const was = before;
    before = now;
    const next = settle(was, now, untrack(() => cursor));
    if (next === untrack(() => cursor)) return;
    model.send({ kind: 'rest', key: next });
    // Its row went away under the focus: the focus follows the cursor. Focus the reader put elsewhere stays there.
    if (inList && (document.activeElement === document.body || !document.activeElement)) void focusStop(next, false);
  });
  $effect(() => {
    const key = cursor;
    try {
      if (key) sessionStorage.setItem(CURSOR_KEY, key);
    } catch {
      /* storage unavailable: the cursor starts at the top next time */
    }
  });

  const stopEl = (key: string | null) => (key ? (list?.querySelector<HTMLElement>(`[data-stop="${CSS.escape(key)}"]`) ?? null) : null);
  const rowOf = (el: Element) => el.closest<HTMLElement>('[data-stop-row]');

  /** Put the focus on stop `key`'s control and show its row (`scroll`). */
  async function focusStop(key: string | null, scroll = true) {
    await tick();
    const el = stopEl(key);
    if (!el) return;
    el.focus({ preventScroll: true, focusVisible: true } as FocusOptions);
    if (scroll) {
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      (rowOf(el) ?? el).scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  function onfocusin(e: FocusEvent) {
    const el = e.target as HTMLElement;
    const row = rowOf(el);
    inList = true;
    if (!row) {
      ring = false;
      return;
    }
    model.send({ kind: 'rest', key: row.dataset.stopRow ?? cursor });
    ring = el.matches(':focus-visible');
  }
  function onfocusout(e: FocusEvent) {
    const to = e.relatedTarget as Node | null;
    if (to && list?.contains(to)) return;
    ring = false;
    // A row removed while focused takes the focus with it, and is detached just after this event: that is not
    // the reader leaving, and the cursor's effect (which runs before the check) moves the focus on.
    const from = e.target as Node;
    queueMicrotask(() => {
      if (from.isConnected) inList = false;
    });
  }

  /** Fold or unfold the stack or run whose stop is `key`. */
  function toggle(key: string) {
    for (const [si, entries] of views.entries())
      for (const e of entries) if (e.kind === 'stack' && e.key === key) return void (folds = toggleFold(folds, sids[si], e.stack));
    const next = new Set(expanded);
    if (!next.delete(key)) next.add(key);
    expanded = next;
  }

  // The dashboard's commands, the page scope here; a command the cursor cannot act on leaves its key its default.
  useKeys({ scope: 'dashboard', guide: DASH_GUIDE, handle: (command) => send(command) });

  /** The row holding the focus, when it is one of the list's. */
  const focusedRow = () => {
    const at = document.activeElement;
    return at && list?.contains(at) ? rowOf(at) : null;
  };

  /** Sends a command, from a key or from a control that does the same. */
  const send = (command: Command) => model.send(command);

  function perform(e: DashEffect) {
    switch (e.kind) {
      case 'show-guide':
        return keyboard.showGuide();
      case 'refresh':
        return refresh();
      case 'focus-filter':
        return void filterBar?.focus();
      case 'focus':
        return void focusStop(e.key);
      case 'toggle':
        return toggle(e.key);
      case 'open-start': {
        const start = views.flat().find((v): v is StackView => v.kind === 'stack' && v.key === e.key)?.stack.start?.row.pr;
        if (start) nav.go(prPath(start.repository.owner.login, start.repository.name, start.number));
        return;
      }
      case 'activate': {
        const el = focusedRow()?.querySelector<HTMLElement>('[data-stop]') ?? null;
        if (el && (el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement)) el.click();
        return;
      }
    }
  }

  // The one quiet line at the end: the viewer's branches without a pull request, in the repositories whose branches were read.
  const branchLine = $derived(branchQuietLine(yoursWithoutPull.counts));
  const addUrl = $derived(installUrl(session.config?.app_slug, session.config?.github_web_url));
  const webUrl = $derived(session.config?.github_web_url ?? 'https://github.com');
  const stage = $derived(accounts.status === 'known' ? accounts.onboarding.state : null);

  function reachText(a: InstalledAccount): string {
    switch (a.reach.kind) {
      case 'suspended':
        return 'Suspended';
      case 'all':
        return 'All repositories';
      case 'selected':
        return a.reach.count === 0
          ? 'No repositories selected that you can see'
          : `${a.reach.count} selected ${a.reach.count === 1 ? 'repository' : 'repositories'}`;
    }
  }

  const personalPolicy = policySummary(PERSONAL_POLICY);
  const OWNER_TEXT = { own: 'Your account', user: 'Personal account', organization: 'Organization' } as const;
</script>

{#snippet addButton(primary: boolean)}
  {#if addUrl}<a class="btn small" class:primary href={addUrl}>Add organization or account</a>{/if}
{/snippet}

{#snippet uncoveredTag(repo: string)}
  {#if isUncovered(repo)}<span class="pill pending app-missing" title={UNCOVERED_TITLE(repo)}
      >app not installed<span class="sr-only">: writes and live updates won't work in {repo}</span></span
    >{/if}
{/snippet}

{#snippet waitTag(row: DashRow)}
  {#if waitUnknown(filter, rowSubject(row))}<span class="pill neutral wait-unknown" title="Who this pull request waits on couldn't be worked out, so the Waiting-on filter can't tell whether it matches. It is shown rather than hidden."
      >waiting on unknown · shown anyway</span
    >{/if}
{/snippet}

{#snippet repoMark(repo: string)}
  <RepoMark {repo} avatarUrl={avatarOf(ownerOf(repo))} />
{/snippet}

{#snippet prCells(row: DashRow, key: string)}
  {@const p = row.pr}
  {@const state = rowMemberState(row, false)}
  {@const ci = ciOf(p)}
  <!-- A single leads with GitHub's own glyph for the pull request, not a rail's node: it is no stack's next step. -->
  <SingleLead
    kind={prKind(p)}
    words="{stateText(state)}, {CHECK_TEXT[ci]}"
    repo={p.repository.nameWithOwner}
    avatarUrl={avatarOf(p.repository.owner.login)}
    number={p.number}
    uncovered={isUncovered(p.repository.nameWithOwner) ? UNCOVERED_TITLE(p.repository.nameWithOwner) : null}
  />
  <div class="pr-row-main">
    <a class="pr-row-title" href={prPath(p.repository.owner.login, p.repository.name, p.number)} data-stop={key} data-map-tone={rowTone(row, me)} data-map-involve={rowInvolve(row, me)} tabindex={key === current ? 0 : -1}>{p.title}</a>
    <div class="pr-row-sub">
      <AuthorMark login={p.author?.login} avatarUrl={p.author?.avatarUrl} />
      <span>updated {relTime(p.updatedAt)}</span>
      {@render involvementPill(p)}
      {@render statePills(row, false)}
      {@render waitTag(row)}
      {@render labelChips(row, key)}
    </div>
  </div>
  <div class="pr-row-side">
    <div class="pr-row-side-top">
      <div class="pr-row-attn" aria-label="Attention set">
        {#if row.attentionError}{@const gap = gapChip(row.attentionError)}<span class="pill neutral" title={gap.title}>{gap.label}</span>{/if}
        {#each row.attention ?? [] as m (m.login)}<b class:me={sameLogin(m.login, me)} title={reasonText(m.reason)}>{m.login}</b>{/each}
        {#if rowWait(row.attention, p.author?.login ?? null, me, now)}<span class="row-wait" title={waitTitle(rowWait(row.attention, p.author?.login ?? null, me, now))}>{rowWait(row.attention, p.author?.login ?? null, me, now)}</span>{/if}
      </div>
      <div class="pr-row-delta mono"><span class="positive">+{p.additions}</span> <span class="negative">−{p.deletions}</span></div>
    </div>
    {@render reviewDetail(row, false)}
  </div>
{/snippet}

<!-- A row's labels, last on its meta line so a row is no taller for them: the first few, then "+N". A chip filters by its label. -->
{#snippet labelChips(row: DashRow, key: string)}
  <LabelList labels={row.pr.labels} max={ROW_LABELS} onpick={addLabel} tabbable={key === current} />
{/snippet}

<!-- How the row involves the viewer (logic/involvement.ts): a review asked of them and assigned to them, assigned only, or (quieter, not their turn) a review asked without an assignment, a team's, or one they gave. -->
{#snippet involvementPill(p: DashPr)}
  {@const inv = involvement(p, me)}
  {#if inv}<span class="pill involve" class:assigned={inv.assigned} class:both={inv.assigned && inv.requested} title={involvementTitle(inv)}><span class="sr-only">{involvementTitle(inv)}</span><span aria-hidden="true">{involvementText(inv)}</span></span>{/if}
{/snippet}

{#snippet statePills(row: DashRow, context: boolean)}
  {@const p = row.pr}
  {@const pill = reviewOf(row, context).pill}
  {#if p.isDraft}<span class="pill neutral">draft</span>{/if}
  {#if p.state === 'MERGED'}<span class="pill merged">merged</span>{:else if p.state === 'CLOSED'}<span class="pill bad">closed</span>{/if}
  {#if pill === 'approved'}<span class="pill">approved</span>{:else if pill === 'changes'}<span class="pill bad">changes requested</span>{/if}
{/snippet}

{#snippet railMember(r: RailPr<DashRow>, st: Stack<DashRow>, key: string)}
  {@const row = r.node.row}
  {@const p = row.pr}
  {@const t = turn(row.attention, p.author?.login ?? null, me)}
  <div class="stack-main">
  <!-- A member from another section is shown for context, and counted there. -->
  <a class="pr-row-title" href={prPath(p.repository.owner.login, p.repository.name, p.number)} data-stop={key} data-map-tone={r.node.context ? undefined : rowTone(row, me)} data-map-involve={r.node.context ? undefined : rowInvolve(row, me)} tabindex={key === current ? 0 : -1}>{p.title}</a>
  <div class="pr-row-sub">
    {#if st.repos.length > 1}{@render repoMark(p.repository.nameWithOwner)}{/if}
    <span class="pr-number mono">#{p.number}</span>
    {#if st.repos.length > 1}{@render uncoveredTag(p.repository.nameWithOwner)}{/if}
    {@render buildsOn(r, st)}
    {#if r.node.context}<span class="stack-elsewhere">not in this section</span>{/if}
    <span class="sr-only">{stateText(prState(r))}, {CHECK_TEXT[ciOf(p)]}</span>
    <AuthorMark login={p.author?.login} avatarUrl={p.author?.avatarUrl} />
    <span>updated {relTime(p.updatedAt)}</span>
    {@render involvementPill(p)}
    {@render statePills(row, r.node.context)}
    {@render waitTag(row)}
    {@render labelChips(row, key)}
  </div>
  </div>
  <div class="stack-side">
  <div class="stack-side-top">
  <div class="stack-turn" aria-label="Attention set">
    {#if row.attentionError}{@const gap = gapChip(row.attentionError)}<span class="pill neutral" title={gap.title}>{gap.label}</span>
    {:else if t.kind === 'you'}<span class="turn-you" title={(row.attention ?? []).map((m) => `${m.login}: ${reasonText(m.reason)}`).join('\n')}
        >{st.start === r.node ? 'start here' : 'your turn'}</span
      >
    {:else if t.kind === 'author'}<span title={row.attention?.[0] ? `${row.attention[0].login}: ${reasonText(row.attention[0].reason)}` : ''}>waiting on author</span>
    {:else if t.kind === 'others'}<span
        >waiting on {#each row.attention ?? [] as m, k (m.login)}{k ? ', ' : ''}<b title={reasonText(m.reason)}>{m.login}</b>{/each}</span
      >{/if}
    {#if !row.attentionError && rowWait(row.attention, p.author?.login ?? null, me, now)}<span class="row-wait" title={waitTitle(rowWait(row.attention, p.author?.login ?? null, me, now))}>{rowWait(row.attention, p.author?.login ?? null, me, now)}</span>{/if}
  </div>
  <div class="pr-row-delta mono"><span class="positive">+{p.additions}</span> <span class="negative">−{p.deletions}</span></div>
  </div>
  {@render reviewDetail(row, r.node.context)}
  </div>
{/snippet}

{#snippet reviewDetail(row: DashRow, context: boolean)}
  {@const p = row.pr}
  {@const head = p.commits.nodes[0]?.commit}
  <ReviewDetail review={row.review} notes={reviewOf(row, context).notes} checks={head?.statusCheckRollup?.state} summary={prSummaries.get(p.repository.nameWithOwner, p.number, head?.oid ?? null)} />
{/snippet}

{#snippet buildsOn(r: RailRow<DashRow>, st: Stack<DashRow>)}
  {@const b = r.base}
  {#if b && !b.above}<span class="stack-from">{buildsOnText(b, st.repos.length > 1)}</span
    >{:else if b && r.elbow}<span class="sr-only">branches off: {buildsOnText(b, st.repos.length > 1)}</span>{/if}
{/snippet}

{#snippet railAside(r: RailAside<DashRow>, st: Stack<DashRow>, sid: string, key: string)}
  {@const elsewhere = r.members.every((m) => m.kind === 'missing' || m.row.outside)}
  {@const filteredOut = r.members.filter((m) => m.kind === 'pr' && m.filtered).length}
  {@const hiddenByFilter = filteredOut === r.members.length}
  {@const note = hiddenByFilter ? 'filtered out' : filteredOut ? 'filtered out or not in this section' : elsewhere ? 'not in your sections' : 'not in this section'}
  {#if r.members.length === 1}
    <div class="stack-aside-text">
      <ContextMember member={r.members[0]} crossRepo={st.repos.length > 1} {note} stop={key} tabbable={key === current} pill={memberPill(r.members[0])} />
      {@render buildsOn(r, st)}
    </div>
  {:else}
    {@const open = expanded.has(key)}
    <div class="stack-aside-text">
      <button
        class="stack-run-toggle"
        aria-expanded={open}
        aria-controls="{key}-members"
        data-stop={key}
        tabindex={key === current ? 0 : -1}
        onclick={() => toggle(key)}
        ><span class="stack-chevron" aria-hidden="true"></span><span class="stack-aside-index mono" aria-hidden="true">{r.index}–{r.to}</span
        >{#if r.first && r.last}<span class="mono">{prLabel(st, r.first)}<span aria-hidden="true"> … </span><span class="sr-only"> to </span>{prLabel(st, r.last)}</span
          >{/if}<span>{r.to - r.index + 1} pull requests</span></button
      >
      {@render buildsOn(r, st)}
      <span class="stack-aside-note">{note}</span>
    </div>
    <ul class="stack-run" id="{key}-members" role="list" hidden={!open}>
      {#each r.members as m (memberStop(sid, st, m))}
        {@const mk = memberStop(sid, st, m)}
        <li class="stack-run-member" class:cursor={ring && mk === current} data-stop-row={mk}>
          <ContextMember member={m} crossRepo={st.repos.length > 1} note={!hiddenByFilter && m.kind === 'pr' && m.filtered ? 'filtered out' : null} stop={mk} tabbable={mk === current} pill={memberPill(m)} />
        </li>
      {/each}
    </ul>
  {/if}
{/snippet}

{#snippet stackItem(v: StackView, si: number, ei: number)}
  {@const st = v.stack}
  {@const id = `stack-${si}-${ei}`}
  {@const open = v.open}
  {@const start = st.start?.row.pr}
  {@const below = belowStart(st)}
  {@const rows = v.rows}
  {@const lines = railLines(rows, rowState)}
  {@const cells = mapCells(rows, prState)}
  {@const counted = rows.flatMap((r) => (r.kind === 'pr' && !r.node.context ? [r.node.row] : []))}
  <li class="stack" class:open>
    <div role="group" aria-labelledby="{id}-label">
      <div class="stack-head" class:cursor={ring && v.key === current} data-stop-row={v.key}>
        <button
          class="stack-toggle"
          aria-expanded={open}
          aria-controls="{id}-rail"
          data-stop={v.key}
          data-map-tone={open ? undefined : topTone(counted.map((row) => rowTone(row, me)))}
          data-map-involve={open ? undefined : topInvolve(counted.map((row) => rowInvolve(row, me)))}
          data-map-title={open ? undefined : `Stack of ${st.count} in ${st.repos.join(', ')}, folded`}
          tabindex={v.key === current ? 0 : -1}
          onclick={() => (folds = toggleFold(folds, sids[si], st))}
        >
          <span class="stack-chevron" aria-hidden="true"></span>
          <!-- Where it is first, in the column a single row names its repository in. -->
          <span class="pr-where">{#each st.repos as repo (repo)}{@render repoMark(repo)}{/each}</span>
          <span class="stack-title" id="{id}-label">Stack of {st.count}</span>
          {#each st.repos as repo (repo)}{#if isUncovered(repo)}<span class="pill pending app-missing" title={UNCOVERED_TITLE(repo)}
                >app not installed{st.repos.length > 1 ? ` on ${repo}` : ''}<span class="sr-only">: writes and live updates won't work there</span></span
              >{/if}{/each}
        </button>
        <StackMap
          {cells}
          label={(c) => cellLabel(st, rows, c)}
          start={st.start?.index ?? null}
          stackLabel="the stack of {st.count}"
          tabbable={cursorTop === v.key}
          onpick={(c) => void goToRow(si, st, id, c.row)}
        />
        <span class="stack-summary">
          {#if st.needsYou}<span class="stack-needs">{st.needsYou} need{st.needsYou === 1 ? 's' : ''} you</span>{/if}
          {#if start}<span
              >start at <a
                class="mono"
                href={prPath(start.repository.owner.login, start.repository.name, start.number)}
                title={start.title}
                tabindex={cursorTop === v.key ? 0 : -1}
                >{st.repos.length > 1 ? start.repository.nameWithOwner : ''}#{start.number}</a
              ></span
            >{#if below}<span class="stack-caveat" title="Whether those wait on you is unknown">{below}</span>{/if}{/if}
          <span>latest update {relTime(st.latest)}</span>
          <span
            class="mono"
            title="Lines added and deleted across the stack's {st.count - st.unloaded - st.hidden - st.filtered} loaded pull requests{st.filtered ? ' the filter shows' : ''}{st.unloaded +
              st.hidden
              ? `; ${st.unloaded + st.hidden} not loaded are not counted`
              : ''}{st.filtered ? `; ${st.filtered} hidden by the filter ${st.filtered === 1 ? 'is' : 'are'} not counted` : ''}"><span class="positive">+{compactCount(st.additions)}</span> <span class="negative">−{compactCount(st.deletions)}</span
            ></span
          >{#if st.filtered}<span class="stack-caveat" title="In the stack, but left out by the filter: not counted in what needs you, where to start, or the lines"
              >{st.filtered} hidden by filter</span
            >{/if}{#if st.unloaded}<span class="stack-caveat">{st.unloaded} not loaded</span>{/if}{#if st.hidden}<span
              class="stack-caveat"
              title="GitHub doesn't show {st.hidden === 1 ? 'it' : 'them'} to you">{st.hidden} hidden from you</span
            >{/if}
        </span>
      </div>
      <ol class="stack-rail" role="list" id="{id}-rail" hidden={!open}>
        {#each rows as r, k (r.kind === 'pr' ? r.node.row.pr.id : `aside:${r.index}`)}
          {@const l = lines[k]}
          {@const key = rowStop(sids[si], st, r)}
          {@const state = rowState(r)}
          {@const ci = r.kind === 'pr' ? ciOf(r.node.row.pr) : 'none'}
          <li
            id="{id}-row-{k}"
            tabindex="-1"
            class="stack-row lane-{r.lane}"
            class:aside={r.kind === 'aside'}
            class:start={r.kind === 'pr' && st.start === r.node}
            class:needs={r.kind === 'pr' && r.node.yours}
            class:cursor={ring && key === current}
            data-stop-row={key}
          >
            <span class="stack-marks" aria-hidden="true"
              >{#if l.trunk?.above}<i class="seg x0 up {l.trunk.above}"></i>{/if}{#if l.trunk?.below}<i class="seg x0 down {l.trunk.below}"></i
                >{/if}{#if l.branch?.above}<i class="seg x1 up {l.branch.above}"></i>{/if}{#if l.branch?.below}<i class="seg x1 down {l.branch.below}"
                ></i>{/if}{#if l.elbow}<i class="stack-elbow {l.elbow}"></i>{/if}<span
                class="stack-node {state} ci-{ci}"
                title="{r.index}{r.to > r.index ? `–${r.to}` : ''} of {st.count}: {stateText(state)}{ci !== 'none' ? `, ${CHECK_TEXT[ci]}` : ''}"
                >{r.kind === 'aside' && r.to > r.index ? '' : r.index}{#if ci === 'failing'}<i class="stack-node-flag">!</i>{/if}</span
              ></span
            >
            <span class="sr-only">{r.to > r.index ? `${r.index} to ${r.to}` : r.index} of {st.count}:</span>
            {#if r.kind === 'pr'}{@render railMember(r, st, key)}{:else}{@render railAside(r, st, sids[si], key)}{/if}
          </li>
        {/each}
      </ol>
    </div>
  </li>
{/snippet}

<header class="top">
  <div class="eyebrow">Dashboard · {me}</div>
  <div class="right">
    <span class="freshness" class:failed={fresh.failure && !fresh.checking} role="status"
      title={fresh.failure ? statusOf(failed(fresh.failure, fresh.retry), 'the latest pull requests', now).summary : fresh.at ? `Read from GitHub ${new Date(fresh.at).toLocaleString()}` : ''}
      >{[
        fresh.failure && !fresh.checking ? "Couldn't refresh" : null,
        fresh.at === null ? null : `updated ${ago(fresh.at)}`,
        fresh.checking ? 'checking…' : null,
      ]
        .filter(Boolean)
        .join(' · ')}</span
    >
    <button class="btn small" onclick={refresh} disabled={dash.refreshing} title="Read every section again from GitHub, using nothing kept in this browser"
      >{dash.refreshing ? 'Refreshing…' : 'Refresh from GitHub'}</button
    >
    <div class="view-switch" role="group" aria-label="Pull request layout">
      <button class:chosen={stacked} aria-pressed={stacked} title="Gather pull requests that build on one another" onclick={() => prefs.set('dashboardView', 'stacked')}>Stacked</button>
      <button class:chosen={!stacked} aria-pressed={!stacked} title="List every pull request on its own" onclick={() => prefs.set('dashboardView', 'flat')}>Flat</button>
    </div>
    <button class="btn small" onclick={startEdit}>Edit sections</button>
  </div>
</header>

<DashFilterBar
  bind:this={filterBar}
  {filter}
  options={facets}
  onchange={setFilter}
  shared={view.shared}
  onsave={() => setFilter(filter)}
  onback={() => writeFilter(mine)}
  save={saveStatus}
  onretry={() => filterSync.retry()}
  summary={filterSummary}
/>

{#if stage === 'not_installed'}
  <div class="banner onboarding" role="note">
    <h3>Install Changeling on an organization or account</h3>
    <p>
      GitHub lists no installation of the Changeling GitHub App on an organization or account where you have a role. If
      the pull requests you review are in private repositories, install it there. If you just installed it, check again.
    </p>
    <p class="actions">
      {@render addButton(true)}
      <button class="btn small" onclick={() => loadAccounts(true)}>Check again</button>
    </p>
  </div>
{:else if stage === 'no_repositories'}
  <div class="banner onboarding" role="note">
    <h3>Choose repositories for Changeling</h3>
    <p>
      Changeling is installed, but GitHub lists no repository of its installations that you can see: each one under
      Accounts is suspended or has no repositories selected that you can access. Choose repositories in an
      installation's settings on GitHub, or ask an admin of the account to, or add another organization or account.
    </p>
    <p class="actions">
      {@render addButton(true)}
      <button class="btn small" onclick={() => loadAccounts(true)}>Check again</button>
    </p>
  </div>
{/if}

{#if editing}
  <section class="card editor">
    <h3>Dashboard sections</h3>
    <p class="muted small">
      One section per line: <code>Title = query || another query</code>. Prefix the title with <code>!</code> to keep only
      pull requests waiting on you. <code>{'{7d}'}</code> expands to the date seven days ago. Saved in this browser.
    </p>
    <textarea rows="8" bind:value={editText} aria-label="Section definitions"></textarea>
    <div class="sheet-actions">
      <button class="btn small" onclick={reset}>Reset to defaults</button>
      <span>
        <button class="btn small" onclick={() => (editing = false)}>Cancel</button>
        <button class="btn small primary" onclick={saveEdit}>Save</button>
      </span>
    </div>
  </section>
{/if}


<p class="sr-only" id="dash-keys-hint">
  In focus mode: j and k move between rows, J and K between stacks and sections, h and l fold and unfold a stack, slash goes to the filters, question mark lists every key.
</p>

<!-- While the saved copy is read (briefly), nothing: its rows then appear in place, moving nothing. With no section's rows to show yet, the loading screen. -->
<LoadingGate phase={!asked || dash.cold ? 'loading' : 'ready'} view={dash.loadView}>
<DashMap ready={filtered.some((f) => f !== null)} {list} filters={mapFilters} />
<div class="dash-lists" bind:this={list} {onfocusin} {onfocusout}>
{#each dash.sections as s, i}
    {@const st = dash.state[i]}
    {@const refresh = st?.tag === 'Has' && st.freshness.kind === 'stale' ? st.freshness.refresh : null}
    {@const busy = !st || st.tag === 'Loading' || st.tag === 'NotAsked' || (st.tag === 'Has' && !!st.value.checking && refresh?.kind !== 'failed')}
    <!-- aria-busy while its rows load or some are checked again: the overview's card for it says so too (DashMap). -->
    <section class="dash-section" class:attention={s.attention} class:stale={refresh?.kind === 'failed'} aria-busy={busy}>
      <div class="sectionhead">
        <h2>{s.title}</h2>
        <span class="eyebrow" class:counting-figure={busy} title={filtering && filtered[i] ? `${filtered[i].rows.length} of this section's ${filtered[i].total} pull requests match the filter` : undefined}
          >{filtered[i] ? countText(filtered[i].rows.length, filtered[i].total, filtering) : ''}</span
        >
      </div>
      {#if !st || st.tag === 'Loading' || st.tag === 'NotAsked'}
        <p class="loading">Loading pull requests…</p>
      {:else if st.tag === 'Failed'}
        <LoadError failure={st.reason} retry={st.retry} what="this section's search" onretry={() => void dash.reloadSection(i)} />
      {:else}
        {@const v = st.value}
        {#if refresh?.kind === 'failed'}
          {@const words = statusOf(has(v, st.freshness), 'this section', now)}
          <p class="muted small stale-note" role="status">
            {words.summary}
            {#if words.next}<span aria-live="off">{words.next}</span>{/if}
            {#if words.advice}{words.advice}{/if}
            <button class="btn small" onclick={() => void dash.reloadSection(i)}>Try again</button>
          </p>
        {/if}
        {#if v.checking}
          <p class="muted small checking-note" role="status">
            {checkingWhat(v.checking, refresh?.kind === 'failed')}
          </p>
        {/if}
        {#if v.note}
          <p class="muted empty-line" role="status">
            {v.note}. They're listed below, each with the reason.
            <button class="btn small" onclick={() => void dash.reloadSection(i)}>Try again</button>
          </p>
        {/if}
        {#if st.gaps}
          <LoadError failure={st.gaps.reason} retry={st.gaps.retry} what={partialWhat(st.gaps.missing)} onretry={() => void dash.reloadSection(i)} />
        {/if}
        {#if (views[i] ?? []).length}
          <ul class="pr-list dash-items" aria-describedby="dash-keys-hint">
            {#each views[i] ?? [] as e, ei (e.kind === 'single' ? e.row.pr.id : `stack:${e.stack.key}`)}
              {#if e.kind === 'single'}
                <li class="pr-row" class:joins-above={shapes[i]?.[ei]?.joinsAbove} class:joins-below={shapes[i]?.[ei]?.joinsBelow} class:mine={hasMember(e.row.attention ?? [], me)} class:cursor={ring && e.key === current} data-stop-row={e.key}>
                  {@render prCells(e.row, e.key)}
                </li>
              {:else}
                {@render stackItem(e, i, ei)}
              {/if}
            {/each}
          </ul>
        {:else if v.rows.length}
          <p class="muted empty-line">
            None of this section's {v.rows.length} pull request{v.rows.length === 1 ? '' : 's'} match{v.rows.length === 1 ? 'es' : ''} the filter.
            <button class="btn small" onclick={() => setFilter(NO_FILTER)}>Clear the filter</button>
          </p>
        {:else if !v.checking}
          <p class="muted empty-line">
            {s.attention ? (st.gaps ? 'Nothing that loaded is waiting on you.' : 'Nothing is waiting on you.') : 'Nothing here.'}
          </p>
        {/if}
        {#if v.cut}
          <p class="muted empty-line" role="note">
            {s.attention ? 'Checked' : 'Showing'} the {v.cut.checked} most recently updated of {v.cut.total} matching pull requests.
          </p>
        {/if}
      {/if}
    </section>
{/each}
</div>

<section class="dash-section accounts" aria-labelledby="accounts-title">
  <div class="sectionhead">
    <h2 id="accounts-title">Accounts</h2>
    {#if stage !== 'not_installed' && stage !== 'no_repositories'}{@render addButton(false)}{/if}
  </div>
  {#if accounts.status === 'loading'}
    <p class="loading">Loading accounts…</p>
  {:else if accounts.status === 'unavailable'}
    <p class="muted empty-line">
      This sign-in can't list the App's installations (it isn't through the Changeling GitHub App), so the accounts it is
      installed on aren't shown.
    </p>
  {:else if accounts.status === 'error'}
    <LoadError failure={accounts.failure} what="the list of the App's installations" onretry={() => loadAccounts(true)} />
  {:else if accounts.onboarding.state === 'not_installed'}
    <p class="muted empty-line">No organization or account yet.</p>
  {:else}
    <ul class="pr-list account-list">
      {#each accounts.onboarding.accounts as a (a.installationId)}
        <li class="pr-row account-row">
          {#if a.avatarUrl}<img class="avatar" src={a.avatarUrl} alt="" />{:else}<span class="avatar" aria-hidden="true"></span>{/if}
          <div class="pr-row-main">
            <span class="pr-row-title">{a.login}</span>
            {#if a.status === 'failed'}
              <div class="pr-row-sub">
                <span>Couldn't look into this installation: {a.message}</span>
                <button class="btn small" onclick={() => loadAccounts(true)}>Try again</button>
              </div>
            {:else}
              <div class="pr-row-sub">
                <span>{OWNER_TEXT[a.owner.kind]}</span>
                {#if a.reach.kind === 'suspended'}<span class="pill bad">suspended</span>{:else}<span>{reachText(a)}</span>{/if}
                {#if a.owner.kind !== 'organization'}<span>{personalPolicy}</span>{/if}
              </div>
            {/if}
          </div>
          {#if a.status === 'listed'}
            {@const policy = policyHref(a)}
            {@const configure = configureUrl(a, webUrl)}
            <div class="account-links">
              {#if policy}<a href={policy} title="{a.login} review policy">Policy</a>{/if}
              {#if configure}<a href={configure} target="_blank" rel="noreferrer" data-external title="The installation's settings on GitHub"
                  >Configure ↗</a
                >{/if}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>
{#if branchLine}<p class="muted dash-quiet"><a href={branchLine.href}>{branchLine.text}</a></p>{/if}
</LoadingGate>
